import { ensureCsrfToken, refreshCsrfToken } from '../auth';
import { notifySessionActive } from '../sessionActivity';

/**
 * Thin fetch wrapper that:
 *   - Sends the auth cookie (credentials: 'include')
 *   - Sends the X-XSRF-TOKEN header on non-safe (POST/PUT/DELETE) requests
 *   - Parses JSON response bodies
 *   - Throws on non-2xx with a structured ApiError so TanStack Query can
 *     show meaningful error states (especially the 409 conflict body which
 *     contains the current lock holder).
 *
 * Identity is no longer header-based — the server identifies the caller via
 * the cookie set by /api/auth/login and the tenant_id claim it carries.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
};

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  // ASP.NET Core anti-forgery validates X-XSRF-TOKEN on non-safe requests.
  // /api/auth/login opts out (no session pre-login), but every other mutation
  // requires it.
  if (!SAFE_METHODS.has(method)) {
    try {
      headers['X-XSRF-TOKEN'] = await ensureCsrfToken();
    } catch {
      // If we couldn't fetch the token (e.g. user not authenticated),
      // let the request through — the server will return 401 and the UI
      // will redirect to login.
    }
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) {
    // 204 is a success — the server renewed the auth cookie via sliding
    // expiration; tell the SessionExpiryWatcher to reset its idle timer.
    notifySessionActive();
    return undefined as T;
  }

  const text = await response.text();
  const parsed = text ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, parsed, `${method} ${path} → ${response.status}`);
  }

  // 2xx: cookie was just slid forward server-side. Reset the client's
  // idle countdown so it agrees with the server's new ExpiresUtc.
  notifySessionActive();
  return parsed as T;
}

function safeJsonParse(text: string): unknown {
  try { return JSON.parse(text); }
  catch { return text; }
}

/**
 * Multipart file upload with progress.
 *
 * Built on XMLHttpRequest rather than fetch, because fetch still has no
 * upload progress event and evidence files run to 25 MB — a plain spinner
 * on a slow connection reads as a hung page. Otherwise it behaves like
 * `api()`: cookie auth, the anti-forgery header, JSON response parsing,
 * and the same ApiError on non-2xx.
 *
 * The Content-Type header is deliberately never set. The browser has to
 * generate it so it can include the multipart boundary; setting it by hand
 * produces a body the server can't parse.
 */
export function apiUpload<T>(
  path: string,
  file: File,
  options: { fieldName?: string; onProgress?: (percent: number) => void } = {},
): Promise<T> {
  const { fieldName = 'file', onProgress } = options;

  return new Promise<T>((resolve, reject) => {
    void (async () => {
      let csrf: string | undefined;
      try {
        // Deliberately a fresh token rather than the cached one. Uploads
        // are the only requests where a stale token is expensive: the
        // whole file is on the wire before the server rejects it. One
        // small GET is cheap insurance against re-sending 25 MB.
        csrf = await refreshCsrfToken();
      } catch {
        // Same posture as api(): let it through and let the server 401,
        // so the UI's existing redirect-to-login path handles it.
      }

      const form = new FormData();
      form.append(fieldName, file, file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', path, true);
      xhr.withCredentials = true;
      if (csrf) xhr.setRequestHeader('X-XSRF-TOKEN', csrf);

      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        const parsed = xhr.responseText ? safeJsonParse(xhr.responseText) : undefined;
        if (xhr.status >= 200 && xhr.status < 300) {
          notifySessionActive();
          resolve(parsed as T);
        } else {
          reject(new ApiError(
            xhr.status, parsed, `POST ${path} → ${xhr.status}`));
        }
      };

      // Network-level failure and user abort both surface as status 0 —
      // there is no response to parse, so give the UI something readable.
      xhr.onerror = () =>
        reject(new ApiError(0, undefined, `POST ${path} → network error`));
      xhr.onabort = () =>
        reject(new ApiError(0, undefined, `POST ${path} → upload cancelled`));

      xhr.send(form);
    })();
  });
}
